import React, { useState, useEffect } from 'react';
import Navigation from '../Navbar/Navbar';
import axios from 'axios';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { Shield, People, Building, Plus, Trash, Database, Download, Upload, Envelope } from 'react-bootstrap-icons';
import EmailSettings from './EmailSettings';
import './Admin.css';

const apiUrl = "/api";

function Admin() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [alert, setAlert] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  // Form states
  const [userForm, setUserForm] = useState({
    id: null,
    name: '',
    username: '',
    password: '',
    role: 'user',
    tenant_id: 1
  });

  const [tenantForm, setTenantForm] = useState({
    name: ''
  });

  // Backup states
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importConsole, setImportConsole] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  // Database maintenance states
  const [cleanLoading, setCleanLoading] = useState(false);
  const [showCleanConfirm, setShowCleanConfirm] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    loadData();
  }, []);

  const checkAdminAccess = () => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      window.location.href = '#/login';
      return;
    }

    const user = JSON.parse(userData);
    if (user.role !== 'admin') {
      window.location.href = '#/';
      return;
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, tenantsRes] = await Promise.all([
        axios.get(`${apiUrl}/admin/users`, { headers }),
        axios.get(`${apiUrl}/admin/tenants`, { headers })
      ]);

      setUsers(usersRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      setAlert({ type: 'danger', message: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${apiUrl}/admin/createuser`, userForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ type: 'success', message: 'User created successfully' });
      setShowUserModal(false);
      setUserForm({ name: '', username: '', password: '', role: 'user', tenant_id: 1 });
      loadData();
    } catch (error) {
      console.error('Error creating user:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to create user'
      });
    }
  };

  const handleCreateTenant = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${apiUrl}/admin/tenants`, tenantForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ type: 'success', message: 'Tenant created successfully' });
      setShowTenantModal(false);
      setTenantForm({ name: '' });
      loadData();
    } catch (error) {
      console.error('Error creating tenant:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to create tenant'
      });
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ type: 'success', message: 'Tenant deleted successfully' });
      loadData();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to delete tenant'
      });
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      id: user.id,
      name: user.name,
      username: user.username,
      password: '', // Don't pre-fill password
      role: user.role,
      tenant_id: user.tenant_id
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ type: 'success', message: 'User deleted successfully' });
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to delete user'
      });
    }
  };

  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = { ...userForm };
      if (!updateData.password) {
        delete updateData.password; // Don't update password if empty
      }

      const response = await axios.put(`${apiUrl}/admin/users/${userForm.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ type: 'success', message: 'User updated successfully' });
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({ id: null, name: '', username: '', password: '', role: 'user', tenant_id: 1 });
      loadData();
    } catch (error) {
      console.error('Error updating user:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to update user'
      });
    }
  };

  // Backup functions
  const loadBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.backups);
    } catch (error) {
      console.error('Error loading backups:', error);
      setAlert({ type: 'danger', message: 'Failed to load backups' });
    }
  };

  const handleCreateBackup = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${apiUrl}/admin/backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ type: 'success', message: 'Database backup created successfully' });
      loadBackups(); // Refresh backup list
    } catch (error) {
      console.error('Error creating backup:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to create backup'
      });
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/admin/backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setAlert({ type: 'success', message: 'Backup downloaded successfully' });
    } catch (error) {
      console.error('Error downloading backup:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to download backup'
      });
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete the backup "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/admin/backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ type: 'success', message: 'Backup deleted successfully' });
      // Refresh the backups list
      loadBackups();
    } catch (error) {
      console.error('Error deleting backup:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to delete backup'
      });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setImportConsole([]);
    setUploadProgress(0);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('dataFile', selectedFile);

    setUploadLoading(true);
    setUploadResult(null);
    setImportConsole([]);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${apiUrl}/admin/upload-data`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          setImportConsole(prev => [...prev, `Upload progress: ${percentCompleted}%`]);
        }
      });

      setUploadResult(response.data);
      setImportConsole(prev => [...prev, `Import completed: ${response.data.message}`]);

      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(error => {
          setImportConsole(prev => [...prev, `Error: ${error}`]);
        });
      }

      setAlert({
        type: response.data.success ? 'success' : 'warning',
        message: response.data.message
      });

      // Clear file input
      setSelectedFile(null);
      const fileInput = document.getElementById('dataFileInput');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Error uploading file:', error);
      setImportConsole(prev => [...prev, `Error: ${error.response?.data?.error || error.message}`]);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to upload file'
      });
    } finally {
      setUploadLoading(false);
      setUploadProgress(0);
    }
  };

  const handleCleanDatabase = async () => {
    setCleanLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${apiUrl}/admin/clean-database`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({
        type: 'success',
        message: response.data.message
      });
      setShowCleanConfirm(false);

    } catch (error) {
      console.error('Error cleaning database:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to clean database'
      });
    } finally {
      setCleanLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navigation />
        <Container className="mt-4">
          <div className="text-center">Loading...</div>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <Container className="page-content-wrapper mt-4">
        <Row className="mb-4">
          <Col>
            <h2 className="admin-title">
              <Shield className="admin-icon" />
              Admin Panel
            </h2>
          </Col>
        </Row>

        {alert && (
          <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        <Row className="mb-4">
          <Col>
            <div className="admin-tabs">
              <Button
                variant={activeTab === 'users' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('users')}
                className="me-2"
              >
                <People className="me-2" />
                User Management
              </Button>
              <Button
                variant={activeTab === 'tenants' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('tenants')}
                className="me-2"
              >
                <Building className="me-2" />
                Tenant Management
              </Button>
              <Button
                variant={activeTab === 'data' ? 'primary' : 'outline-primary'}
                onClick={() => {
                  setActiveTab('data');
                  loadBackups();
                }}
                className="me-2"
              >
                <Database className="me-2" />
                Data Management
              </Button>
              <Button
                variant={activeTab === 'maintenance' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('maintenance')}
                className="me-2"
              >
                <Trash className="me-2" />
                Database Maintenance
              </Button>
              <Button
                variant={activeTab === 'email' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('email')}
              >
                <Envelope className="me-2" />
                Email Settings
              </Button>
            </div>
          </Col>
        </Row>

        {activeTab === 'users' && (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="mb-0">Users</h5>
              <Button variant="success" onClick={() => setShowUserModal(true)} className="mt-2 mt-md-0">
                <Plus className="me-2" />
                Add User
              </Button>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Tenant</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.username}</td>
                      <td>
                        <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-secondary'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>{tenants.find(t => t.id === user.tenant_id)?.name || 'Unknown'}</td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          className="me-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.role === 'admin'}
                        >
                          <Trash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {activeTab === 'tenants' && (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="mb-0">Tenants</h5>
              <Button variant="success" onClick={() => setShowTenantModal(true)} className="mt-2 mt-md-0">
                <Plus className="me-2" />
                Add Tenant
              </Button>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(tenant => (
                    <tr key={tenant.id}>
                      <td>{tenant.name}</td>
                      <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                      <td>
                        {tenant.id !== 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteTenant(tenant.id)}
                          >
                            <Trash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {activeTab === 'data' && (
          <div>
            {/* Database Backup Section */}
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="mb-0">
                  <Database className="me-2" />
                  Database Backup
                </h5>
                <Button
                  variant="success"
                  onClick={handleCreateBackup}
                  disabled={backupLoading}
                  className="mt-2 mt-md-0"
                >
                  {backupLoading ? 'Creating...' : 'Create Backup'}
                </Button>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">
                  Create a full database backup that can be downloaded and restored later.
                </p>
                {backups.length > 0 ? (
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Filename</th>
                        <th>Size</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.map(backup => (
                        <tr key={backup.filename}>
                          <td>{backup.filename}</td>
                          <td>{(backup.size / 1024 / 1024).toFixed(2)} MB</td>
                          <td>{new Date(backup.created).toLocaleString()}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleDownloadBackup(backup.filename)}
                              className="me-2"
                            >
                              <Download className="me-1" />
                              Download
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteBackup(backup.filename)}
                            >
                              <Trash className="me-1" />
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted">
                    No backups found. Create your first backup above.
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Data Import Section */}
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <Upload className="me-2" />
                  Data Import
                </h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">
                  Upload CSV, JSON, or SQL files to import data into all tables except users.
                  Supported formats: .csv, .json, .sql
                </p>

                <Form.Group className="mb-3">
                  <Form.Label>Select Data File</Form.Label>
                  <Form.Control
                    id="dataFileInput"
                    type="file"
                    accept=".csv,.json,.sql"
                    onChange={handleFileSelect}
                    disabled={uploadLoading}
                  />
                  <Form.Text className="text-muted">
                    Select a file to import. Data will be automatically imported to all applicable tables.
                  </Form.Text>
                </Form.Group>

                {selectedFile && (
                  <div className="mb-3">
                    <strong>Selected File:</strong> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    <br />
                    <Button
                      variant="primary"
                      onClick={handleFileUpload}
                      disabled={uploadLoading}
                      className="mt-2"
                    >
                      <Upload className="me-2" />
                      Apply Import
                    </Button>
                  </div>
                )}

                {uploadLoading && (
                  <div className="mb-3">
                    <div className="mb-2">
                      <strong>Upload Progress:</strong>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {uploadProgress}%
                      </div>
                    </div>
                    <div className="text-center mt-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Processing...</span>
                      </div>
                      <div className="mt-1">Processing file...</div>
                    </div>
                  </div>
                )}

                {importConsole.length > 0 && (
                  <div className="mb-3">
                    <strong>Import Console:</strong>
                    <div
                      className="border rounded p-2 mt-2"
                      style={{
                        height: '200px',
                        overflowY: 'auto',
                        backgroundColor: '#f8f9fa',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }}
                    >
                      {importConsole.map((line, index) => (
                        <div key={index} className="text-dark">
                          {line}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadResult && (
                  <Alert variant={uploadResult.success ? 'success' : 'warning'}>
                    <strong>Import Results:</strong> {uploadResult.message}
                    {uploadResult.recordsProcessed && (
                      <div className="mt-2">
                        <strong>Records Processed:</strong> {uploadResult.recordsProcessed}
                        {uploadResult.recordsSuccessful && (
                          <> | <strong>Successful:</strong> {uploadResult.recordsSuccessful}</>
                        )}
                        {uploadResult.recordsFailed && (
                          <> | <strong>Failed:</strong> {uploadResult.recordsFailed}</>
                        )}
                      </div>
                    )}
                  </Alert>
                )}

                <Alert variant="info">
                  <strong>Import Guidelines:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Auto-Import:</strong> Data will be imported to all applicable tables automatically</li>
                    <li><strong>CSV files:</strong> Must contain columns matching table schemas</li>
                    <li><strong>JSON files:</strong> Must be an array of objects with matching properties</li>
                    <li><strong>SQL files:</strong> Must contain valid INSERT statements</li>
                    <li><strong>File Size Limit:</strong> Maximum 100MB per file</li>
                    <li><strong>Allowed Formats:</strong> Only .csv, .json, and .sql files are permitted</li>
                    <li><strong>Note:</strong> Records are imported with tenant_id = 1 by default unless specified in the data</li>
                  </ul>
                </Alert>
              </Card.Body>
            </Card>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div>
            <Card className="border-danger">
              <Card.Header className="bg-danger text-white">
                <h5 className="mb-0">
                  <Trash className="me-2" />
                  Database Cleaning
                </h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="warning">
                  <strong>⚠️ Warning: This action cannot be undone!</strong>
                  <p className="mb-0 mt-2">
                    This will permanently delete all data from the following tables:
                  </p>
                  <ul className="mb-0 mt-2">
                    <li>Items (inventory)</li>
                    <li>Staff Members (agents)</li>
                    <li>Signouts</li>
                    <li>Signout Details</li>
                    <li>COA List</li>
                    <li>Computer Configurations</li>
                    <li>Consumable Item Names</li>
                    <li>Installed Software</li>
                    <li>License Types</li>
                    <li>Licenses</li>
                    <li>System Logs</li>
                  </ul>
                  <p className="mb-0 mt-2">
                    <strong>The users table will be preserved.</strong> Any records with missing or invalid tenant_id will be assigned to tenant_id = 1 (dependableIT).
                  </p>
                </Alert>

                <div className="text-center mt-4">
                  <Button
                    variant="outline-danger"
                    size="lg"
                    onClick={() => setShowCleanConfirm(true)}
                    disabled={cleanLoading}
                  >
                    <Trash className="me-2" />
                    Clean Database
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
      </Container>

      {/* User Creation/Edit Modal */}
      <Modal show={showUserModal} onHide={() => {
        setShowUserModal(false);
        setEditingUser(null);
        setUserForm({ id: null, name: '', username: '', password: '', role: 'user', tenant_id: 1 });
      }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? 'Edit User' : 'Create New User'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password {editingUser && '(leave blank to keep current)'}</Form.Label>
              <Form.Control
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                required={!editingUser}
                placeholder={editingUser ? 'Leave blank to keep current password' : ''}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tenant</Form.Label>
              <Form.Select
                value={userForm.tenant_id}
                onChange={(e) => setUserForm({ ...userForm, tenant_id: parseInt(e.target.value) })}
              >
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowUserModal(false);
            setEditingUser(null);
            setUserForm({ id: null, name: '', username: '', password: '', role: 'user', tenant_id: 1 });
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={editingUser ? handleUpdateUser : handleCreateUser}>
            {editingUser ? 'Update User' : 'Create User'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tenant Creation Modal */}
      <Modal show={showTenantModal} onHide={() => setShowTenantModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Tenant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Tenant Name</Form.Label>
              <Form.Control
                type="text"
                value={tenantForm.name}
                onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                placeholder="Enter tenant name"
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTenantModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateTenant}>
            Create Tenant
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Database Cleaning Confirmation Modal */}
      <Modal show={showCleanConfirm} onHide={() => setShowCleanConfirm(false)}>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>⚠️ Confirm Database Cleaning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>This action will permanently delete ALL data from the following tables:</strong>
            <ul className="mb-0 mt-2">
              <li>Items (inventory)</li>
              <li>Staff Members (agents)</li>
              <li>Signouts</li>
              <li>Signout Details</li>
              <li>COA List</li>
              <li>Computer Configurations</li>
              <li>Consumable Item Names</li>
              <li>Installed Software</li>
              <li>License Types</li>
              <li>Licenses</li>
              <li>System Logs</li>
            </ul>
          </Alert>
          <p className="mt-3 mb-0">
            <strong>The users table will remain intact.</strong> Any records with missing tenant_id will be assigned to tenant_id = 1 (dependableIT).
          </p>
          <p className="mt-3 mb-0 text-danger">
            <strong>This action cannot be undone. Are you absolutely sure?</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCleanConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleCleanDatabase}
            disabled={cleanLoading}
          >
            {cleanLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Cleaning Database...
              </>
            ) : (
              <>
                <Trash className="me-2" />
                Yes, Clean Database
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Email Settings Tab */}
      {activeTab === 'email' && (
        <EmailSettings tenantId={parseInt(localStorage.getItem('selectedTenant')) || 1} />
      )}
    </div>
  );
}

export default Admin;